export {
  WsClient,
  ERROR_CODES,
  JSONRPC_VERSION,
  type RpcRequest,
  type RpcResponse,
  type RpcSuccessResponse,
  type RpcErrorResponse,
  type RpcId,
  type WsStatus,
  type WsClientOptions,
} from './wsClient.js';

export { Dispatcher, ToolError, type ToolHandler } from './dispatcher.js';

export {
  store,
  setStudioContext,
  getStudioContext,
  type PlaceholderProject,
  type PlaceholderTrack,
  type PlaceholderMarker,
  type StudioContextLike,
} from './handlers/store.js';
export {
  createOpenDawAdapter,
  makeStudioContext,
  type OpenDawAdapterOptions,
  type MakeStudioContextOptions,
} from './openDawAdapter.js';
export { trackHandlers, registerTrackHandlers } from './handlers/tracks.js';
export { markerHandlers, registerMarkerHandlers } from './handlers/markers.js';
export { sessionHandlers, registerSessionHandlers } from './handlers/session.js';
export { analysisHandlers, registerAnalysisHandlers } from './handlers/analysis.js';
export { insertHandlers, registerInsertHandlers } from './handlers/inserts.js';

import { Dispatcher } from './dispatcher.js';
import { WsClient, type WsClientOptions, type WsStatus } from './wsClient.js';
import { registerMarkerHandlers } from './handlers/markers.js';
import { registerSessionHandlers } from './handlers/session.js';
import { registerTrackHandlers } from './handlers/tracks.js';
import { registerAnalysisHandlers } from './handlers/analysis.js';
import { registerInsertHandlers } from './handlers/inserts.js';
import { setStudioContext, type StudioContextLike } from './handlers/store.js';

export type AgentTokenHandler = (token: string, meta?: unknown) => void;
export type AgentFinalHandler = (text: string, meta?: unknown) => void;

export type AgentEvent =
  | { type: 'token'; text: string }
  | { type: 'tool_call_started'; name: string; arguments: string; reasoning?: string }
  | { type: 'tool_call_succeeded'; name: string; result: unknown }
  | { type: 'tool_call_failed'; name: string; error: string }
  | { type: 'final_text'; text: string }
  | { type: 'error'; error: string }
  | { type: 'iteration_limit_reached'; iterations: number };

export type AgentEventHandler = (event: AgentEvent) => void;

export interface ChatSendResult {
  ok: boolean;
  text?: string;
  error?: string;
}

export interface BootstrapOptions {
  wssUrl: string;
  getJwt: () => string | Promise<string>;
  wsClient?: WsClientOptions;
  // INT01 passes the live openDAW studio context here so the agent's tool
  // handlers read/write real project state. If omitted, the bridge falls
  // back to the in-memory placeholder store (used by tests and pre-INT01
  // bring-up).
  studioContext?: StudioContextLike;
}

export interface AgentBridge {
  client: WsClient;
  dispatcher: Dispatcher;
  chatSend: (
    text: string,
    historyHint?: ReadonlyArray<{ role: string; content: string }>,
    projectSnapshot?: string,
  ) => Promise<ChatSendResult>;
  onAgentToken: (cb: AgentTokenHandler) => void;
  onAgentFinal: (cb: AgentFinalHandler) => void;
  onAgentEvent: (cb: AgentEventHandler) => void;
  onStatus: (cb: (s: WsStatus) => void) => void;
  close: () => void;
}

export function bootstrap(opts: BootstrapOptions): AgentBridge {
  // Attach the studio context (if supplied) before registering handlers so
  // any eager reads see the live project from the first call.
  setStudioContext(opts.studioContext ?? null);

  const dispatcher = new Dispatcher();
  registerSessionHandlers(dispatcher);
  registerTrackHandlers(dispatcher);
  registerMarkerHandlers(dispatcher);
  registerAnalysisHandlers(dispatcher);
  registerInsertHandlers(dispatcher);

  const client = new WsClient(opts.wsClient);

  client.onIncoming(async (req) => dispatcher.dispatch(req));

  let tokenHandler: AgentTokenHandler | null = null;
  let finalHandler: AgentFinalHandler | null = null;
  let eventHandler: AgentEventHandler | null = null;

  client.onNotification((method, params) => {
    if (method === 'agent_event') {
      const event = params as AgentEvent;
      if (eventHandler) eventHandler(event);
      if (event && typeof event === 'object') {
        if (event.type === 'token' && tokenHandler) {
          tokenHandler(event.text, undefined);
        } else if (event.type === 'final_text' && finalHandler) {
          finalHandler(event.text, undefined);
        }
      }
      return;
    }
    // Legacy notifications kept for backwards compatibility with the old
    // (pre-FIX01) protocol. They will be removed once all clients move to
    // agent_event.
    if (method === 'agent_token' && tokenHandler) {
      const p = (params ?? {}) as { token?: string; text?: string; meta?: unknown };
      const t = typeof p.token === 'string' ? p.token : typeof p.text === 'string' ? p.text : '';
      tokenHandler(t, p.meta);
      return;
    }
    if (method === 'agent_final' && finalHandler) {
      const p = (params ?? {}) as { text?: string; meta?: unknown };
      finalHandler(typeof p.text === 'string' ? p.text : '', p.meta);
      return;
    }
  });

  client.connect(opts.wssUrl, opts.getJwt);

  async function chatSend(
    text: string,
    historyHint?: ReadonlyArray<{ role: string; content: string }>,
    projectSnapshot?: string,
  ): Promise<ChatSendResult> {
    const resp = await client.send<{ ok?: boolean; text?: string; error?: string }>({
      method: 'userMessage',
      params: {
        text,
        ...(historyHint && historyHint.length > 0 ? { history: historyHint } : {}),
        ...(projectSnapshot ? { projectSnapshot } : {}),
      },
    });
    if ('error' in resp) {
      throw new Error(`userMessage failed: ${resp.error.message}`);
    }
    const result = resp.result ?? {};
    if (result.ok === false) {
      throw new Error(result.error ?? 'userMessage failed');
    }
    return {
      ok: true,
      ...(typeof result.text === 'string' ? { text: result.text } : {}),
    };
  }

  return {
    client,
    dispatcher,
    chatSend,
    onAgentToken(cb) {
      tokenHandler = cb;
    },
    onAgentFinal(cb) {
      finalHandler = cb;
    },
    onAgentEvent(cb) {
      eventHandler = cb;
    },
    onStatus(cb) {
      client.onStatus(cb);
    },
    close() {
      client.close();
      setStudioContext(null);
    },
  };
}
